import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  OnDestroy,
  inject,
  effect,
  computed,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { SentryService } from '@core/services/diagnostics/sentry.service';
// import { GoogleAnalyticsService } from '@core/services/diagnostics/google-analytics.service';
import { AppConfigService } from '@core/services/config/app-config.service';
import { LogRocketService } from '@core/services/diagnostics/log-rocket.service';
import { VersionCheckService } from '@core/services/version-check.service';
import { Store } from '@ngrx/store';
import {
  AUTH_ALERT_COOKIE_KEY,
  COOKIE_ALERT_COOKIE_KEY,
  coreActions,
  ShowAuthAlertBannerStatus,
  ShowCookieAlertBannerStatus,
} from '@core/actions/core.actions';
import {
  combineLatest,
  delay,
  filter,
  map,
  Observable,
  BehaviorSubject,
} from 'rxjs';
import {
  getShouldDisplayAuthAlertBanner,
  getShouldDisplayCookieAlertBanner,
} from '@core/state/ui/application/application-ui.selectors';
import { selectProjectListingsAreLoading } from '@core/state/ui/project-index/project-index-ui.selectors';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { selectProjectIsLoading } from '@core/state/ui/project/project-ui.selectors';
import { selectChosenStudyIsLoading } from '@core/state/ui/stage/stage-ui.selectors';
import { AsyncPipe, Location } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NavComponent } from './core/nav/nav.component';
import {
  NavigationEnd,
  PRIMARY_OUTLET,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { GridModule } from '@ngbracket/ngx-layout/grid';
import { DomSanitizer } from '@angular/platform-browser';
import { CookieService } from 'ngx-cookie-service';
import {
  base64UrlSegmentEncode,
  base64UrlSegmentParse,
} from './core/serializers/base-64-guid-url-serializer';
import { FeatureToggleService } from './core/services/feature-toggle/feature-toggle.service';
import {
  VERSION_INFO_DIALOG_TITLE,
  VersionInfoDialogComponent,
} from './shared/dialogs/version-info-dialog/version-info-dialog.component';
import { SyrfDialogService } from './shared/dialog/syrf-dialog.service';
import {
  environmentActions,
  selectEnvironmentType,
  selectShowWarningBanner,
} from '@core/state/environment';
import { EnvironmentBannerComponent } from '@core/components/environment-banner/environment-banner.component';
import { EnvironmentIndicatorComponent } from '@core/components/environment-indicator/environment-indicator.component';
import {
  EnvironmentWarningDialogComponent,
} from '@core/components/environment-warning-dialog/environment-warning-dialog.component';
import { ImpersonationBannerComponent } from '@core/components/impersonation-banner/impersonation-banner.component';
import { LayoutService } from '@core/services/layout/layout.service';
import { CookieConsentBannerComponent } from '@core/components/cookie-consent-banner/cookie-consent-banner.component';
import { ThemeService } from '@core/services/theme/theme.service';

/** Delay in ms before showing the environment warning dialog to let the app render first */
const ENVIRONMENT_WARNING_DIALOG_DELAY_MS = 500;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    GridModule,
    FlexLayoutModule,
    MatIconModule,
    MatButtonModule,
    RouterLink,
    NavComponent,
    MatProgressBarModule,
    RouterOutlet,
    MatDividerModule,
    MatSnackBarModule,
    AsyncPipe,
    EnvironmentBannerComponent,
    EnvironmentIndicatorComponent,
    ImpersonationBannerComponent,
    CookieConsentBannerComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  displayAuthAlertBanner$: Observable<boolean>;
  displayCookieBanner$: Observable<boolean>;
  version: string;
  privacyUrl: string;
  blurCover = false;
  showLoadingIndicator$: Observable<boolean>;
  projectsLoading$: Observable<boolean>;
  projectLoading$: Observable<boolean>;
  studyLoading$: Observable<boolean>;
  private _snackBar = inject(MatSnackBar);
  private readonly _hostRef = inject(ElementRef<HTMLElement>);
  private readonly _syrfDialog = inject(SyrfDialogService);
  private readonly _environmentType = inject(Store).selectSignal(
    selectEnvironmentType
  );
  private readonly layoutService = inject(LayoutService);
  private readonly _themeService = inject(ThemeService);
  /**
   * The footer CAMARADES logo for the effective theme, without its file
   * extension: white lettering on dark, the original black lettering otherwise.
   */
  readonly camaradesLogoBase = computed(() =>
    this._themeService.isDark()
      ? 'assets/images/Camarades-Logo-dark'
      : 'assets/images/Camarades-Logo'
  );
  /** The footer NC3Rs brandmark for the effective theme: white on dark, ultramarine otherwise. */
  readonly nc3rsBrandmarkSrc = computed(() =>
    this._themeService.isDark()
      ? 'assets/images/NC3Rs_brandmark_white.svg'
      : 'assets/images/NC3Rs_brandmark_ultramarine.svg'
  );
  /**
   * Published on the shell as `--app-header-height` so anything that pins
   * itself while the PAGE scrolls can clear the sticky app header.
   *
   * The header is `position: sticky; top: 0` at `z-index: 100`, so a
   * descendant that also sticks at `top: 0` slides underneath it and is simply
   * not visible. Its height is not a constant either -- the environment and
   * impersonation banners are part of the same sticky block -- so the measured
   * value is the only correct offset, and `LayoutService` already measures it
   * with a `ResizeObserver`.
   */
  protected readonly headerHeight = this.layoutService.headerHeight;
  private readonly _router = inject(Router);
  /**
   * Source of the *initial* value of `_shellUrl` below, and so of
   * `isBarePdfViewerRoute` and `sourceWindowRoute`.
   *
   * `Router.url` is still `'/'` while these class fields initialise: the app
   * boots with non-blocking initial navigation, so the first `NavigationEnd`
   * has not happened yet. Seeding the signal from it made a popup opened
   * directly at `/pdf-viewer#<capability>` render the whole shell -- banners,
   * nav, loading bar, footer, cookie banner -- for the frames before that
   * navigation completed, and the toolbar/footer registration effects then
   * registered elements that were destroyed a frame later. `Location.path()`
   * reports the current browser URL (base href stripped, same shape as
   * `Router.url`) synchronously, before any navigation has run.
   */
  private readonly _location = inject(Location);

  /**
   * The shell's current URL: seeded synchronously from `Location` (see the
   * `_location` comment above), then the redirected URL of every
   * `NavigationEnd`. The single router subscription every route predicate
   * below derives from.
   */
  private readonly _shellUrl = toSignal(
    this._router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects)
    ),
    { initialValue: this._location.path() }
  );

  /** True only for the bare `study-source-window` route. */
  readonly sourceWindowRoute = computed(() => {
    const segments = this._router.parseUrl(this._shellUrl()).root.children[
      PRIMARY_OUTLET
    ]?.segments;
    return segments?.length === 1 && segments[0].path === 'study-source-window';
  });

  /**
   * True only for the bare `/pdf-viewer` popup route (path only -- the
   * capability token used by the postMessage protocol lives in the URL
   * fragment and is deliberately ignored here). The full app shell -- the
   * environment/impersonation banners, main nav, loading bar, footer, and
   * cookie-consent banner -- is suppressed for that route so the integrated
   * PDF viewer popup (ADR-014) renders only its own content, with no live
   * `routerLink`s that could navigate the popup away from the viewer.
   */
  readonly isBarePdfViewerRoute = computed(() =>
    this._isBarePdfViewerUrl(this._shellUrl())
  );

  /**
   * Reference to the header container for layout tracking.
   *
   * A signal query, not `@ViewChild` read once in `ngAfterViewInit`: the
   * toolbar and footer live inside `@if (!isBarePdfViewerRoute())`, so their
   * views are destroyed and re-created as the router moves on and off the
   * bare `/pdf-viewer` popup route. A one-shot read captured `undefined` on
   * the bare route and then kept a stale (detached) node afterwards, leaving
   * LayoutService observing an element that is no longer in the document.
   */
  readonly appToolbar = viewChild<ElementRef<HTMLElement>>('appToolbar');
  /** Reference to the footer container for layout tracking. See `appToolbar`. */
  readonly appFooter = viewChild<ElementRef<HTMLElement>>('appFooter');

  /**
   * (Re-)register the header and footer with LayoutService whenever the
   * conditional shell views are created. `registerHeaderElement` /
   * `registerFooterElement` disconnect any previous ResizeObserver first, so
   * re-running on re-creation swaps the observed node rather than stacking
   * observers. A destroyed view leaves the query `undefined`; the last known
   * heights are then left in place rather than being reset, because the bare
   * viewer route has no chrome for anything to position against anyway.
   */
  private readonly _headerRegistrationEffect = effect(() => {
    const toolbar = this.appToolbar()?.nativeElement;
    if (toolbar) {
      this.layoutService.registerHeaderElement(toolbar);
    }
  });
  private readonly _footerRegistrationEffect = effect(() => {
    const footer = this.appFooter()?.nativeElement;
    if (footer) {
      this.layoutService.registerFooterElement(footer);
    }
  });

  /**
   * D17 — moves keyboard focus to the routed main content. The href stays a real fragment so the
   * control is announced as a link, but navigation is prevented: letting the browser follow
   * `#main-content` would hand the router a fragment-only URL and lose the current route.
   */
  skipToMainContent(event: Event): void {
    event.preventDefault();
    const main = (this._hostRef.nativeElement as HTMLElement).querySelector(
      '#main-content'
    ) as HTMLElement | null;
    main?.focus();
    main?.scrollIntoView?.();
  }

  // Environment info for templates - using store selector as signal
  readonly showEnvironmentBanner = this._store.selectSignal(
    selectShowWarningBanner
  );

  // Track if warning dialog has been shown this session
  private warningDialogShown = false;
  private warningDialogTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Use effect to reactively show warning dialog when environment info loads
  private readonly warningDialogEffect = effect(() => {
    // A source-only window must not acquire navigation chrome or an automatic
    // environment modal. Keep normal routes' existing warning behavior.
    if (this.sourceWindowRoute()) {
      if (this.warningDialogTimeoutId !== null) {
        clearTimeout(this.warningDialogTimeoutId);
        this.warningDialogTimeoutId = null;
        this.warningDialogShown = false;
      }
      return;
    }
    // Read signal value reactively from store
    const shouldShow = this.showEnvironmentBanner();
    // Read reactively too, so a navigation onto the bare route re-runs this.
    const isBareViewer = this.isBarePdfViewerRoute();

    if (isBareViewer) {
      // The bare `/pdf-viewer` popup deliberately renders no app shell, so an
      // environment warning dialog there is orphaned chrome on top of the
      // PDF. Suppressing the banner markup alone was not enough -- the dialog
      // is opened from here, not from the template. Cancel a dialog already
      // scheduled by an earlier run if the route turned bare inside the 500ms
      // delay window.
      this._cancelPendingWarningDialog();
      return;
    }

    // Only show once, when data is loaded and conditions are met
    if (
      shouldShow &&
      !this.warningDialogShown &&
      !AppConfigService.staticConfig.suppressEnvironmentWarning &&
      EnvironmentWarningDialogComponent.shouldShowWarning()
    ) {
      this.warningDialogShown = true;
      // Small delay to let the app fully render first
      this.warningDialogTimeoutId = setTimeout(() => {
        this.warningDialogTimeoutId = null;
        // Re-check rather than trusting the scheduling-time decision: the
        // route can turn bare after this timeout was armed but before it
        // fires, and the effect's cancellation above is not the only path
        // here (a navigation that does not re-run this effect still must not
        // pop a dialog over the popup).
        if (this.isBarePdfViewerRoute()) {
          // The dialog is not being shown, so release the one-shot latch here
          // too. `_cancelPendingWarningDialog` cannot do it for us: this timer
          // has already cleared `warningDialogTimeoutId`, so the effect's later
          // cancellation is a no-op, and the window would never be owed its
          // warning again. Reachable whenever the route turns bare between the
          // timer firing and the effect running -- under zoneless change
          // detection effects flush in `afterNextRender`, not as microtasks.
          this.warningDialogShown = false;
          return;
        }
        EnvironmentWarningDialogComponent.open(
          this._syrfDialog,
          this._environmentType(),
          { disableClose: false }
        );
      }, ENVIRONMENT_WARNING_DIALOG_DELAY_MS);
    }
  });

  // Add reactive state for app unstable warning
  private _appIsUnstable = new BehaviorSubject<boolean>(false);
  public appIsUnstable$ = this._appIsUnstable.asObservable();
  private unstableStateListener?: (event: Event) => void;

  constructor(
    sentryService: SentryService,
    // googleAnalytics: GoogleAnalyticsService,
    logRocket: LogRocketService,
    _configService: AppConfigService,
    public appElement: ElementRef,
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer,
    private _store: Store,
    _appConfig: AppConfigService,
    _featureToggleService: FeatureToggleService,
    _cookieService: CookieService,
    private versionCheckService: VersionCheckService
  ) {
    // PDF.js worker configured lazily in PdfLoaderService

    window.savedConsole = console;
    window.base64UrlSegmentEncode = base64UrlSegmentEncode;
    window.base64UrlSegmentParse = base64UrlSegmentParse;

    const authAlertCookieVal = _cookieService.get(AUTH_ALERT_COOKIE_KEY);
    const cookieAlertCookieVal = _cookieService.get(COOKIE_ALERT_COOKIE_KEY);
    _store.dispatch(
      coreActions.coreInit({
        authAlertStatus:
          ShowAuthAlertBannerStatus[
            authAlertCookieVal !== undefined &&
            authAlertCookieVal !== null &&
            ShowAuthAlertBannerStatus[authAlertCookieVal]
              ? authAlertCookieVal
              : 'true'
          ],
        cookieAlertBannerStatus:
          ShowCookieAlertBannerStatus[
            cookieAlertCookieVal !== undefined &&
            cookieAlertCookieVal !== null &&
            ShowCookieAlertBannerStatus[cookieAlertCookieVal]
              ? cookieAlertCookieVal
              : 'true'
          ],
      })
    );
    const appConfig = _appConfig.config;
    iconRegistry.addSvgIcon(
      'syrf-title',
      sanitizer.bypassSecurityTrustResourceUrl(
        `${appConfig.baseUrl}/assets/images/SyRF-Title.svg`
      )
    );
    iconRegistry.addSvgIcon(
      'ml',
      sanitizer.bypassSecurityTrustResourceUrl(
        `${appConfig.baseUrl}/assets/images/MLIcon.svg`
      )
    );
    iconRegistry.addSvgIcon(
      'syrf-icon',
      sanitizer.bypassSecurityTrustResourceUrl(
        `${appConfig.baseUrl}/assets/images/SyRF_circle.svg`
      )
    );
    iconRegistry.addSvgIcon(
      'syrf-front',
      sanitizer.bypassSecurityTrustResourceUrl(
        `${appConfig.baseUrl}/assets/images/SyRF_circle_front.svg`
      )
    );
    iconRegistry.addSvgIcon(
      'external-link-white',
      sanitizer.bypassSecurityTrustResourceUrl(
        `${appConfig.baseUrl}/assets/images/external-link-white.svg`
      )
    );
    iconRegistry.addSvgIcon(
      'external-link-black',
      sanitizer.bypassSecurityTrustResourceUrl(
        `${appConfig.baseUrl}/assets/images/external-link-black.svg`
      )
    );
    iconRegistry.addSvgIcon(
      'x-logo',
      sanitizer.bypassSecurityTrustResourceUrl(
        `${appConfig.baseUrl}/assets/images/x-logo.svg`
      )
    );
    iconRegistry.addSvgIcon(
      'google',
      sanitizer.bypassSecurityTrustResourceUrl(
        `${appConfig.baseUrl}/assets/images/google.svg`
      )
    );
    iconRegistry.addSvgIcon(
      'apple',
      sanitizer.bypassSecurityTrustResourceUrl(
        `${appConfig.baseUrl}/assets/images/apple.svg`
      )
    );
    iconRegistry.addSvgIcon(
      'facebook',
      sanitizer.bypassSecurityTrustResourceUrl(
        `${appConfig.baseUrl}/assets/images/facebook.svg`
      )
    );
    iconRegistry.addSvgIcon(
      'collapse',
      sanitizer.bypassSecurityTrustResourceUrl(
        `${appConfig.baseUrl}/assets/images/collapse.svg`
      )
    );
    iconRegistry.addSvgIcon(
      'placeholder-dot',
      sanitizer.bypassSecurityTrustResourceUrl(
        `${appConfig.baseUrl}/assets/images/placeholder-dot.svg`
      )
    );
    iconRegistry.addSvgIcon(
      'visibility-settings',
      sanitizer.bypassSecurityTrustResourceUrl(
        `${appConfig.baseUrl}/assets/icons/visibility-settings.svg`
      )
    );
    iconRegistry.addSvgIcon(
      'document-locked',
      sanitizer.bypassSecurityTrustResourceUrl(
        `${appConfig.baseUrl}/assets/icons/document-locked.svg`
      )
    );
    const defaultFontSetClasses = iconRegistry.getDefaultFontSetClass();
    const outlinedFontSetClasses = defaultFontSetClasses
      .filter((fontSetClass) => fontSetClass !== 'material-icons')
      .concat(['material-symbols-outlined']);
    iconRegistry.setDefaultFontSetClass(...outlinedFontSetClasses);
    sentryService.init();
    // googleAnalytics.init();
    // LogRocket initialization now lazy via dynamic import inside service
    logRocket.init();
    this.version = _configService.config.displayVersion;
    this.privacyUrl = `${_configService.config.stsUrl}/Privacy`;
    this.displayAuthAlertBanner$ = this._store.select(
      getShouldDisplayAuthAlertBanner
    );
    this.displayCookieBanner$ = this._store.select(
      getShouldDisplayCookieAlertBanner
    );
    this.projectsLoading$ = this._store.select(selectProjectListingsAreLoading);
    this.projectLoading$ = this._store.select(selectProjectIsLoading);
    this.studyLoading$ = this._store.select(selectChosenStudyIsLoading);
    this.showLoadingIndicator$ = combineLatest([
      this.projectsLoading$,
      this.projectLoading$,
      this.studyLoading$,
    ]).pipe(
      map(
        ([projectsLoading, projectLoading, studyLoading]) =>
          projectsLoading || projectLoading || studyLoading
      ),
      delay(0)
    );
  }

  ngOnInit(): void {
    // Note: Environment info is loaded via EnvironmentEffects.ngrxOnInitEffects()
    // to ensure the action is dispatched after effects are registered.
    // This avoids timing issues with the APP_BOOTSTRAP_LISTENER mechanism.

    // The bare `/pdf-viewer` popup renders no app shell, so it shows no
    // startup crash notices either. It shares the opener's origin and so its
    // localStorage: the flags are left alone for the main window to report
    // (the unresolved-error snackbar never auto-dismisses and its action
    // would reload the popup).
    if (!this.isBarePdfViewerRoute()) {
      this.checkForCrashRecovery();
      this.checkForUnresolvedError();
    }
    this.setupUnstableStateListener();
    // Note: Environment warning dialog is handled reactively via warningDialogEffect
  }

  ngOnDestroy(): void {
    if (this.unstableStateListener) {
      window.removeEventListener(
        'app-unstable-state',
        this.unstableStateListener
      );
    }
    // Cancel any pending warning dialog timeout to prevent errors during teardown
    this._cancelPendingWarningDialog();
  }

  /** Cancel a warning dialog that is inside its render delay, if any. */
  private _cancelPendingWarningDialog(): void {
    if (this.warningDialogTimeoutId !== null) {
      clearTimeout(this.warningDialogTimeoutId);
      this.warningDialogTimeoutId = null;
      // The dialog never actually appeared, so the "already shown once this
      // session" latch must not stay set: a window that turns bare inside the
      // 500ms delay and later navigates back to an ordinary route is still
      // owed its environment warning. Reset only when a pending timeout was
      // genuinely cancelled -- an already-opened dialog must not re-open.
      this.warningDialogShown = false;
    }
  }

  /** Matches only the router URL's path segment, ignoring query and fragment. */
  private _isBarePdfViewerUrl(url: string): boolean {
    return url.split(/[?#]/)[0] === '/pdf-viewer';
  }

  private setupUnstableStateListener(): void {
    this.unstableStateListener = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.showWarning) {
        this._appIsUnstable.next(true);
      }
    };
    window.addEventListener('app-unstable-state', this.unstableStateListener);
  }

  private checkForCrashRecovery(): void {
    const recoveredFromCrash = localStorage.getItem('app_recovered_from_crash');

    if (recoveredFromCrash === 'true') {
      this._snackBar.open(
        '✅ SyRF has recovered from a reported error',
        'DISMISS',
        {
          duration: 8000,
          panelClass: ['success-snackbar'],
        }
      );

      // Remove the flag so the message doesn't show again
      localStorage.removeItem('app_recovered_from_crash');
    }
  }

  private checkForUnresolvedError(): void {
    const errorDataStr = localStorage.getItem('app_error_occurred');

    if (errorDataStr) {
      let errorOccurred = false;
      let errorTimestamp = 0;

      // Handle both old string format and new object format for backward compatibility
      try {
        const errorData = JSON.parse(errorDataStr);
        // Check if it's an object with our expected structure (new format)
        if (
          typeof errorData === 'object' &&
          errorData !== null &&
          'occurred' in errorData
        ) {
          errorOccurred = errorData.occurred === true;
          errorTimestamp = errorData.timestamp || 0;
        } else {
          // Legacy format - JSON.parse succeeded but it's not our object structure
          errorOccurred = errorDataStr === 'true';
          errorTimestamp = 0;
        }
      } catch {
        // JSON.parse failed - legacy format
        errorOccurred = errorDataStr === 'true';
        errorTimestamp = 0;
      }

      if (errorOccurred) {
        const navigationEntry = performance.getEntriesByType(
          'navigation'
        )[0] as PerformanceNavigationTiming | undefined;
        const isManualRefresh = navigationEntry?.type === 'reload';

        // If it's been more than 30 seconds since error occurred, consider it a manual refresh
        const timeSinceError = Date.now() - errorTimestamp;
        const isStaleError = errorTimestamp > 0 && timeSinceError > 30000; // 30 seconds

        if (isManualRefresh || isStaleError) {
          // Clear the error flag silently - manual refresh should provide clean slate
          localStorage.removeItem('app_error_occurred');
          return;
        }

        // Show warning for recent same-session errors and update unstable state
        this._appIsUnstable.next(true);

        const snackBarRef = this._snackBar.open(
          '⚠️ Application may be unstable due to an earlier error. Some features may not work correctly until you refresh.',
          'REFRESH NOW',
          {
            duration: 0, // Don't auto-dismiss
            panelClass: ['warning-snackbar'],
          }
        );

        // Handle the action button click
        snackBarRef.onAction().subscribe(() => {
          localStorage.setItem('app_recovered_from_crash', 'true');
          localStorage.removeItem('app_error_occurred');
          this._appIsUnstable.next(false);
          window.location.reload();
        });

        // Auto-remove the flag after 5 minutes if user doesn't interact
        setTimeout(
          () => {
            localStorage.removeItem('app_error_occurred');
            this._appIsUnstable.next(false);
            snackBarRef.dismiss();
          },
          5 * 60 * 1000
        ); // 5 minutes
      }
    }
  }

  get currentYear(): number {
    return new Date().getFullYear();
  }

  @HostListener('window:scroll', [])
  onScroll() {
    const appToolbar = document.getElementById('app-toolbar');
    if (document.body.scrollTop > 8 || document.documentElement.scrollTop > 8) {
      appToolbar?.classList.add('shadow');
    } else {
      appToolbar?.classList.remove('shadow');
    }
  }

  dismissBanner() {
    this._store.dispatch(
      coreActions.setAuthAlertDisplayStatus({
        status: ShowAuthAlertBannerStatus.false,
      })
    );
  }

  dismissCookieBanner() {
    this._store.dispatch(
      coreActions.setCookieAlertDisplayStatus({
        status: ShowCookieAlertBannerStatus.false,
      })
    );
  }

  openVersionInfo(): void {
    this._syrfDialog.open(VersionInfoDialogComponent, {
      mode: 'adaptive',
      title: VERSION_INFO_DIALOG_TITLE,
      width: '450px',
      autoFocus: false,
    });
  }

  dismissEnvironmentBanner(): void {
    this._store.dispatch(environmentActions.dismissBanner());
  }

  openEnvironmentWarning(): void {
    EnvironmentWarningDialogComponent.open(
      this._syrfDialog,
      this._environmentType()
    );
  }
}
